import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

import Button from '../../components/ui/Button';
import MetricsCards from './components/MetricsCards';
import PerformanceChart from './components/PerformanceChart';
import CategoryBreakdown from './components/CategoryBreakdown';
import FilterControls from './components/FilterControls';
import { offerService, Offer } from '../../services/offerService';
import { supabase } from '../../lib/supabase';

import {
  OfferMetrics,
  CategoryPerformance,
  PerformanceData,
  FilterOptions
} from './types';
import LiveOffersManagementCard from './components/LiveOffersManagementCard';
import OffersTable from './components/OffersTable';

const OfferManagement = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    category: 'all',
    location: 'all',
    status: 'all',
    dateRange: 'month'
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [actualMetrics, setActualMetrics] = useState<OfferMetrics>({
    totalOffers: 0,
    activeOffers: 0,
    totalRevenue: 0,
    averageROI: 0,
    totalQRScans: 0,
    totalRedemptions: 0
  });
  const [categoryPerformanceData, setCategoryPerformanceData] = useState<CategoryPerformance[]>([]);

  // Load offers and analytics data from database
  const loadOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load offers data
      const offersData = await offerService.getAllOffers();
      setOffers(offersData);

      // CRITICAL FIX: Get ACTUAL redemption totals from database
      // This query handles deleted offers correctly by reading saved snapshot data
      const { data: redemptionTotals, error: totalsError } = await supabase
        .from('redemptions')
        .select('final_price, saved_amount, offer_category');

      let actualTotalRevenue = 0;
      let actualTotalSavings = 0;
      let actualTotalRedemptions = 0;
      const categoryRedemptionMap = new Map<string, { revenue: number; redemptions: number }>();

      if (!totalsError && redemptionTotals) {
        redemptionTotals.forEach(redemption => {
          const revenue = redemption.final_price || 0;
          const savings = redemption.saved_amount || 0;
          const category = redemption.offer_category || 'Other';

          actualTotalRevenue += revenue;
          actualTotalSavings += savings;
          actualTotalRedemptions++;

          // Track category-level metrics
          const categoryData = categoryRedemptionMap.get(category) || { revenue: 0, redemptions: 0 };
          categoryData.revenue += revenue;
          categoryData.redemptions++;
          categoryRedemptionMap.set(category, categoryData);
        });
      }

      // Calculate metrics with actual redemption data
      const activeOffers = offersData.filter(offer => offer.status === 'active');
      const calculatedMetrics: OfferMetrics = {
        totalOffers: offersData.length,
        activeOffers: activeOffers.length,
        totalRevenue: actualTotalRevenue,
        averageROI: actualTotalRevenue > 0 ? (actualTotalRevenue / offersData.length) * 0.28 : 0,
        totalQRScans: Math.round(actualTotalRedemptions * 1.2),
        totalRedemptions: actualTotalRedemptions
      };
      setActualMetrics(calculatedMetrics);

      // Calculate category performance with actual redemption data
      const categoryMap = new Map<string, {
        offers: number;
        revenue: number;
        redemptions: number;
      }>();

      // Initialize categories from offers
      offersData.forEach(offer => {
        const category = offer.category || 'Other';
        const current = categoryMap.get(category) || { offers: 0, revenue: 0, redemptions: 0 };
        categoryMap.set(category, {
          offers: current.offers + 1,
          revenue: current.revenue,
          redemptions: current.redemptions
        });
      });

      // Add actual redemption data to categories
      categoryRedemptionMap.forEach((data, category) => {
        const categoryData = categoryMap.get(category);
        if (categoryData) {
          categoryData.revenue = data.revenue;
          categoryData.redemptions = data.redemptions;
        } else {
          // Category exists in redemptions but not in current offers (deleted offers)
          categoryMap.set(category, {
            offers: 0,
            revenue: data.revenue,
            redemptions: data.redemptions
          });
        }
      });

      const colors = ['#178EEF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4'];
      let colorIndex = 0;

      const categoryPerformance = Array.from(categoryMap.entries()).map(([category, data]) => ({
        category,
        offers: data.offers,
        revenue: data.revenue,
        redemptions: data.redemptions,
        avgRedemptionAmount: data.redemptions > 0 ? data.revenue / data.redemptions : 0,
        color: colors[colorIndex++ % colors.length]
      }));
      setCategoryPerformanceData(categoryPerformance);

      // Load enhanced performance chart data with 4 meaningful metrics
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 30); // Last 30 days for daily trends
      
      // Get comprehensive performance analytics including new metrics
      const performanceAnalytics = await offerService.getPerformanceAnalytics(startDate, endDate);
      
      // Process performance analytics into chart-ready data
      const dailyData: PerformanceData[] = performanceAnalytics.map(analytics => ({
        date: new Date(analytics.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: analytics.revenue,
        redemptionsPerDay: analytics.redemptions,
        userEngagementScore: analytics.userEngagementScore,
        averageSavingsPerUser: analytics.averageSavingsPerUser
      }));

      // If no data, create sample points for the last 7 days with meaningful default values
      if (dailyData.length === 0) {
        const sampleData: PerformanceData[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          sampleData.push({
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            revenue: 0,
            redemptionsPerDay: 0,
            userEngagementScore: 0,
            averageSavingsPerUser: 0
          });
        }
        setPerformanceData(sampleData);
      } else {
        setPerformanceData(dailyData);
      }

    } catch (error: any) {
      console.error('Error loading offers:', error);
      setError(error.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  // Handle offer status toggle
  const handleToggleOfferStatus = async (offer: Offer) => {
    try {
      const newStatus = offer.status === 'active' ? 'inactive' : 'active';
      await offerService.updateOffer(offer.id, { status: newStatus });
      await loadOffers(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating offer status:', error);
      setError(error.message || 'Failed to update offer status');
    }
  };

  // Handle offer deletion
  const handleDeleteOffer = async (offerId: string) => {
    try {
      await offerService.deleteOfferPermanently(offerId);
      await loadOffers(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting offer:', error);
      setError(error.message || 'Failed to delete offer');
    }
  };

  // Add bulk operation handlers
  const handleBulkActivate = async (offerIds: string[]) => {
    try {
      await offerService.bulkUpdateOfferStatus(offerIds, 'active');
      await loadOffers(); // Refresh the list
    } catch (error: any) {
      console.error('Error bulk activating offers:', error);
      setError(error.message || 'Failed to activate offers');
    }
  };

  const handleBulkDeactivate = async (offerIds: string[]) => {
    try {
      await offerService.bulkUpdateOfferStatus(offerIds, 'inactive');
      await loadOffers(); // Refresh the list
    } catch (error: any) {
      console.error('Error bulk deactivating offers:', error);
      setError(error.message || 'Failed to deactivate offers');
    }
  };

  const handleBulkDelete = async (offerIds: string[]) => {
    try {
      await offerService.bulkDeleteOffers(offerIds);
      await loadOffers(); // Refresh the list
    } catch (error: any) {
      console.error('Error bulk deleting offers:', error);
      setError(error.message || 'Failed to delete offers');
    }
  };

  // Calculate metrics from live data - CRITICAL FIX: Use actual redemption data instead of potential savings
  const calculateMetricsFromOffers = (offersData: Offer[]): OfferMetrics => {
    const activeOffers = offersData.filter(offer => offer.status === 'active');
    
    // CRITICAL FIX: Don't estimate from moneySaved - that's POTENTIAL savings, not actual
    // Instead, we'll fetch actual redemption metrics separately in loadOffers
    
    return {
      totalOffers: offersData.length,
      activeOffers: activeOffers.length,
      totalRevenue: 0, // Will be populated from actual redemptions
      averageROI: 0, // Will be calculated from actual data
      totalQRScans: 0, // Will be calculated from actual data
      totalRedemptions: 0 // Will be populated from actual redemptions
    };
  };

  // Calculate category performance from live data
  const calculateCategoryPerformance = (offersData: Offer[]): CategoryPerformance[] => {
    const categoryMap = new Map<string, {
      offers: number;
      revenue: number;
      redemptions: number;
    }>();

    // CRITICAL FIX: Don't use moneySaved for revenue calculation
    // We'll fetch actual redemption data per category from the database
    offersData.forEach(offer => {
      const category = offer.category || 'Other';
      const current = categoryMap.get(category) || { offers: 0, revenue: 0, redemptions: 0 };
      
      categoryMap.set(category, {
        offers: current.offers + 1,
        revenue: 0, // Will be populated from actual redemptions
        redemptions: 0 // Will be populated from actual redemptions
      });
    });

    const colors = ['#178EEF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4'];
    let colorIndex = 0;

    return Array.from(categoryMap.entries()).map(([category, data]) => ({
      category,
      offers: data.offers,
      revenue: data.revenue,
      redemptions: data.redemptions,
      avgRedemptionAmount: data.redemptions > 0 ? data.revenue / data.redemptions : 0,
      color: colors[colorIndex++ % colors.length]
    }));
  };

  // Handle offer preview
  const handlePreviewOffer = (offer: Offer) => {
    // Create a modal or navigate to a preview page
    console.log('Preview offer:', offer);
    // You can implement a modal here or navigate to a preview route
  };

  // Handle offer edit with immediate reload to prevent stale data
  const handleEditOffer = (offer: Offer) => {
    // Route to admin create offer screen instead of partner create offer screen
    // This ensures admins use their dedicated interface with full control
    window.location.href = `/admin-create-offer?edit=${offer.id}`;
  };

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
    setSidebarCollapsed(false);
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const handleRefresh = () => {
    loadOffers();
  };

  // CRITICAL FIX: Use actual metrics from database instead of calculated estimates
  const metrics = actualMetrics;
  const categories = categoryPerformanceData;

  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading offers...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleRefresh}>Try Again</Button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Offer Management</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive offer performance analytics and optimization insights
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button 
                onClick={() => navigate('/admin-create-offer')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create New Offer
              </Button>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <span>Vienna Time:</span>
                <span className="font-mono">
                  {new Date().toLocaleTimeString('en-US', {
                    timeZone: 'Europe/Vienna',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} CET
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <FilterControls
          filters={filters}
          onFiltersChange={setFilters}
          onRefresh={handleRefresh} />

        {/* Metrics Cards - Now with live data */}
        <div data-metrics-cards>
          <MetricsCards metrics={metrics} />
        </div>

        {/* Main Content Grid - Updated for full width on smaller screens */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Performance Chart - Full width on mobile/tablet, half width on XL+ */}
          <div className="w-full">
            <PerformanceChart data={performanceData} />
          </div>

          {/* Category Breakdown - Full width on mobile/tablet, half width on XL+ */}
          <div className="w-full">
            <CategoryBreakdown categories={categories} />
          </div>
        </div>

        {/* Live Offers Management Card - Enhanced */}
        <div className="mb-6">
          <LiveOffersManagementCard 
            offers={offers}
            onRefresh={handleRefresh}
          />
        </div>

        {/* Complete Offers Table */}
        <div className="mb-6">
          <OffersTable 
            offers={offers}
            onToggleStatus={handleToggleOfferStatus}
            onDelete={handleDeleteOffer}
            onBulkActivate={handleBulkActivate}
            onBulkDeactivate={handleBulkDeactivate}
            onBulkDelete={handleBulkDelete}
            loading={loading}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Offer Management - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="Comprehensive offer management and performance optimization dashboard" />
      </Helmet>

      <div className="p-6">
        {renderContent()}
      </div>
    </>
  );
};

export default OfferManagement;